import {
  Button,
  Callout,
  Classes,
  Dialog,
  FormGroup,
  InputGroup,
  Intent,
  Radio,
  RadioGroup,
} from "@blueprintjs/core"
import { FormEvent, useState } from "react"

import { CreateApiKeyPayload, CreatedApiKey } from "../../../../api/apiKeys"
import { TokenBox } from "./ApiKeys.styled"

export enum ExpiryChoice {
  ONE_YEAR = "one_year",
  CUSTOM = "custom",
  NEVER = "never",
}

interface ICreateApiKeyDialogProps {
  isOpen: boolean
  onClose: () => void
  onCreate: (payload: CreateApiKeyPayload) => void
  isCreating: boolean
  /** Set after a successful create — switches the dialog to one-time token display */
  createdKey: CreatedApiKey | null
  onCopyToken: (token: string) => void
}

export const CreateApiKeyDialog = ({
  isOpen,
  onClose,
  onCreate,
  isCreating,
  createdKey,
  onCopyToken,
}: ICreateApiKeyDialogProps) => {
  const [name, setName] = useState("")
  const [expiryChoice, setExpiryChoice] = useState<ExpiryChoice>(ExpiryChoice.ONE_YEAR)
  const [customDate, setCustomDate] = useState("")

  const close = () => {
    setName("")
    setExpiryChoice(ExpiryChoice.ONE_YEAR)
    setCustomDate("")
    onClose()
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const payload: CreateApiKeyPayload = { name: name.trim() }
    if (expiryChoice === ExpiryChoice.NEVER) {
      payload.expires_at = null
    } else if (expiryChoice === ExpiryChoice.CUSTOM) {
      // Valid through the chosen day — not dead at its first midnight
      payload.expires_at = new Date(`${customDate}T23:59:59`).toISOString()
    }
    onCreate(payload)
  }

  const submitDisabled =
    isCreating || name.trim() === "" || (expiryChoice === ExpiryChoice.CUSTOM && customDate === "")

  return (
    <Dialog
      title={createdKey ? "Token created" : "New MCP token"}
      className="bp5-dark"
      isOpen={isOpen}
      onClose={close}
      canOutsideClickClose={!createdKey}
    >
      {createdKey ? (
        <>
          <div className={Classes.DIALOG_BODY}>
            <Callout intent={Intent.WARNING} title="You won't see this token again">
              Copy it now and store it safely — it cannot be retrieved later.
            </Callout>
            <TokenBox data-testid="plaintext-token">{createdKey.token}</TokenBox>
            <p>Connect your MCP client by sending the token as a bearer header:</p>
            <TokenBox>{`Authorization: Bearer ${createdKey.token}`}</TokenBox>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button
                icon="clipboard"
                text="Copy token"
                onClick={() => onCopyToken(createdKey.token)}
              />
              <Button intent={Intent.PRIMARY} text="Done" onClick={close} />
            </div>
          </div>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className={Classes.DIALOG_BODY}>
            <FormGroup label="Name" labelInfo="(required)">
              <InputGroup
                placeholder="e.g. Claude Code laptop"
                value={name}
                onChange={event => setName(event.target.value)}
                autoFocus
              />
            </FormGroup>
            <RadioGroup
              label="Expiry"
              selectedValue={expiryChoice}
              onChange={event => setExpiryChoice(event.currentTarget.value as ExpiryChoice)}
            >
              <Radio label="1 year (default)" value={ExpiryChoice.ONE_YEAR} />
              <Radio label="Custom date" value={ExpiryChoice.CUSTOM} />
              <Radio label="Never" value={ExpiryChoice.NEVER} />
            </RadioGroup>
            {expiryChoice === ExpiryChoice.CUSTOM && (
              <FormGroup label="Expires on">
                <input
                  type="date"
                  className={Classes.INPUT}
                  value={customDate}
                  onChange={event => setCustomDate(event.target.value)}
                  data-testid="custom-expiry-date"
                />
              </FormGroup>
            )}
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button text="Cancel" onClick={close} />
              <Button
                type="submit"
                intent={Intent.PRIMARY}
                text="Create token"
                disabled={submitDisabled}
                loading={isCreating}
              />
            </div>
          </div>
        </form>
      )}
    </Dialog>
  )
}
